// src/components/user/UserProfileDropdown.tsx
import React, { useState, useRef, useEffect } from 'react';
import { ChevronDown, User, CreditCard, LogOut } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/Layout/Card';
import { Divider } from '@/components/ui/Layout/Divider';

interface UserProfileDropdownProps {
  user: {
    name?: string;
    email?: string;
  };
  onLogout: () => void;
}

const UserProfileDropdown: React.FC<UserProfileDropdownProps> = ({ user, onLogout }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t } = useTranslation();
  
  // Закрываем дропдаун при клике вне компонента
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const handleNavigate = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };
  
  // Используем email из email или email
  const displayEmail = user.email || user.email || '';
  
  return (
    <div className="relative" ref={dropdownRef}>
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center text-gray-700 hover:text-green-700 px-4 py-2 rounded-lg hover:bg-green-50"
      >
        <span>{displayEmail}</span>
        <ChevronDown className={`ml-1 w-4 h-4 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>
      
      {isOpen && (
        <Card 
          className="absolute top-full right-0 mt-2 w-48 z-10" 
          padding="none" 
          shadow="md"
        >
          <div className="py-1">
            <button 
              onClick={() => handleNavigate('/personal/profile')}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <User className="w-4 h-4 mr-2" />
              {t('profile.title')}
            </button>
            
            <button 
              onClick={() => handleNavigate('/personal/user-settings')}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
            >
              <CreditCard className="w-4 h-4 mr-2" />
              {t('profile.subscriptions')}
            </button>
            
            <Divider margin="sm" />
            
            <button 
              onClick={onLogout}
              className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center"
            >
              <LogOut className="w-4 h-4 mr-2" />
              {t('auth.logout')}
            </button>
          </div>
        </Card>
      )}
    </div>
  );
};

export default UserProfileDropdown;